import { Button, Form, Input, message, Modal, Spin, TreeSelect } from "antd";
import { SidepanelStyles } from "constants/index";
import { useContext, useEffect, useMemo, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  getTreeFromMeasuremntList,
  UnitType
} from "pages/material-details/material-details-tab/units-tree";
import { QUERY_MASTER_UNITS } from "services/graphQL/queries";
import { MaterialExtractSpecType } from "../model";
import { TrackAsSingleMaterialAPI } from "../auth/extraction-auth";

export default function TrackAsSingleMaterialDrawer({
  openDrawer,
  onCancel,
  onComplete,
  selectedRows,
  materialHeaderMap
}: {
  openDrawer: boolean;
  onCancel: () => void;
  onComplete: () => void;
  selectedRows: MaterialExtractSpecType[];
  materialHeaderMap: any;
}) {
  const [loading, setLoading] = useState(false);
  const { gqlClientForProject, tokenRetrievalState } =
    useContext(ProjectContext);
  const [form] = Form.useForm();
  useEffect(() => {
    if (openDrawer && selectedRows?.length > 0) {
      const firstRow = selectedRows[0];

      form.setFieldsValue({
        title: firstRow?.title || "",
        manufacturer: firstRow?.manufacturer || "",
        tag: firstRow?.tag || "",
        quantity: selectedRows?.length
      });
    }
  }, [openDrawer, selectedRows, form]);

  const { data: allUnits } = useCIQQuery<{
    material_quantity_unit: Array<UnitType>;
    material_size_unit: Array<UnitType>;
  }>(QUERY_MASTER_UNITS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject
  });

  const quantityUnitTreeOptions = useMemo(
    () =>
      allUnits
        ? getTreeFromMeasuremntList(allUnits.material_quantity_unit)
        : [],
    [allUnits]
  );

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      const extractionRows = selectedRows;
      const firstRow = extractionRows[0];
      const res = await TrackAsSingleMaterialAPI(
        {
          extraction_data_id: firstRow.id,
          title: values.title,
          tag_name: firstRow.tag,
          manufacturer: firstRow.manufacturer,
          combined_extraction_ids: extractionRows
            .map((row) => row.id)
            .filter((x) => x !== firstRow.id),
          quantity: values.quantity,
          quantity_unit_id: values.quantity_unit_id,
          discription: `Material Tags included: ${extractionRows
            .map((row) => row.tag)
            .join(", ")}`
        },
        tokenRetrievalState?.token
      );
      if (res.status !== 200) {
        message.error("Failed to create material");
      } else {
        message.success("Material created successfully");
        onComplete();
      }
    } catch (error) {
      console.error("Error creating material:", error);
    }
    setLoading(false);
  };

  return (
    <Modal
      className="custom-drawer"
      title={[
        <div data-testid="tractSinglematerialstatus-modal">
          <span>Track as Single Material</span>
          <span className="text-xs font-normal ml-2 mr-2">|</span>
          <span className="text-xs font-normal">
            {selectedRows.length} Materials Selected
          </span>
        </div>
      ]}
      width={420}
      style={SidepanelStyles.style}
      footer={[
        <section className="submittal-modal-footer " key={100}>
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            htmlType="submit"
            type="primary"
            disabled={loading}
            loading={loading}
            onClick={handleSubmit}
          >
            Confirm
          </Button>
        </section>
      ]}
      open={openDrawer}
      onCancel={() => {
        onCancel();
      }}
      destroyOnClose
      data-testid="index-modal-extract-material-single"
    >
      <div className="flex flex-col">
        <Form className="flex flex-col space-y-2" layout="vertical" form={form}>
          <div className="flex flex-col pb-2">
            <div className="font-semibold text-black text-opacity-90">
              Material Information
            </div>
            <div className="text-xs text-black text-opacity-50">
              Material Tags of individual Materials will be added in the
              Description of the new Material being created.
            </div>
          </div>

          <Form.Item
            label={materialHeaderMap?.name || "Material Name"}
            name="title"
            required
          >
            <Input type="text" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-2">
            <Form.Item
              label={materialHeaderMap?.quantity}
              data-testid="quantity-form-item"
              name="quantity"
            >
              <Input
                type="number"
                min={0}
                placeholder="Enter Quantity"
                className="w-full"
                data-testid="quantity-input"
              />
            </Form.Item>
            <Form.Item
              label={materialHeaderMap?.quantity_unit_id}
              data-testid="quantity-unit-form-item"
              name="quantity_unit_id"
              initialValue={16}
            >
              {quantityUnitTreeOptions.length > 0 ? (
                <TreeSelect
                  data-testid="quantity-unit-dropdown"
                  treeNodeLabelProp="label"
                  placeholder="Enter Quantity Unit"
                  // value={editMaterialData.quantity_unit_id}
                  treeData={quantityUnitTreeOptions}
                  // onChange={(value: any) => {
                  //   setEditMaterialData((prev: any) => ({
                  //     ...prev,
                  //     quantity_unit_id: value
                  //   }));
                  // }}
                  treeDefaultExpandAll
                  loading={quantityUnitTreeOptions.length === 0}
                />
              ) : (
                <Spin />
              )}
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
